import {
  FluidFormElementState,
  FluidFormState,
  FluidKeyedObject,
  FluidLocale,
  FluidRowData,
  FluidTableHeader,
  FluidTableHeaderType,
  FluidTableRowData,
  FluidTruncationOptions,
  isBooleanValue,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import { FluidFormatters } from '@lmig/fluid-core/lib/utils/formatting.utils';

import {
  FORM_EXPANDED,
  ID,
  PATH_DELIM,
  SELECTED,
  TABLE_ROW_ID_DEFAULT,
  VISIBLE,
} from '../../../constants/constants';
import { formatCurrency } from '../../../utils/formatting/formatting.utils';
import { Utils } from '../../../utils/utils';
import { FluidTable } from '../fluid-table';
import { setGroupedDataSource } from './filter/fluid-table-filter.utils';
import { formExpanded } from './fluid-table-form.utils';
import { convertNonBooleanIndicatorToBoolean } from './fluid-table-select.utils';

/**
 * Get the cell data for display, based on header config
 * @param dataItem
 * @param header
 */
export const cellData = <DataItem extends FluidTableRowData<DataItem>>(
  dataItem: DataItem,
  header: FluidTableHeader
) => {
  const dataValue = header.dataPath
    ? getDataAtPath(dataItem, header)
    : dataItem[header.dataKey];
  return header.dataFormat
    ? header.dataFormat(header.useFullDataItem ? dataItem : dataValue)
    : header.formattingOptions
    ? applyTypeFormatting(header, dataValue)
    : dataValue;
};

/**
 * Applies formatting to the display value based on the column type and formatting
 * options
 * @param header
 * @param valueToFormat
 */
export const applyTypeFormatting = (
  header: FluidTableHeader,
  valueToFormat: any
) => {
  switch (header.type) {
    case FluidTableHeaderType.CURRENCY:
      return formatCurrency(valueToFormat, header.formattingOptions.currency);
    case FluidTableHeaderType.NUMBER:
      return FluidFormatters.intlNumberFormat(
        valueToFormat,
        header.formattingOptions.number
      );
    case FluidTableHeaderType.DATE:
      return FluidFormatters.formatDate(
        valueToFormat,
        header.formattingOptions.date?.locale || FluidLocale.DEFAULT,
        header.formattingOptions.date.dateFormat
      );
    default:
      return valueToFormat;
  }
};

/**
 * Applies truncation maxLength to a cell value (if value is array, joins value with a comma @TODO - configurable)
 * @param options
 * @param header
 * @param dataItem
 * @param value
 */
export const getTruncatedValue = (
  options: FluidTruncationOptions,
  header: FluidTableHeader,
  dataItem: FluidRowData,
  value?: any
): string => {
  const cellValue = value || cellData(dataItem, header);
  const handleStringConversion = (cellValue) =>
    Array.isArray(cellValue)
      ? (cellValue as Array<any>).join(', ')
      : `${cellValue}`;
  const addEllipsis = (cellValue) =>
    handleStringConversion(cellValue).length <= options.maxLength ? '' : '...';
  return `${handleStringConversion(cellValue).substring(
    0,
    options.maxLength
  )}${addEllipsis(cellValue)}`;
};

/**
 * Applies the default values for dataItems for the table to
 * control its state from.
 * @param dataItems
 * @param tableId
 * @param rowIdentifier
 * @param expandedForms
 * @param selectableDataPath
 */
export const applyTableReferences = <
  DataItem extends FluidTableRowData<DataItem>
>(
  dataItems: DataItem[],
  tableId: string,
  rowIdentifier?: keyof DataItem,
  expandedForms?: FluidKeyedObject<boolean>,
  selectableDataPath?: string
) => {
  const canUseIdentifier = canUseIdentifiers(dataItems, rowIdentifier);

  return (dataItems || []).map((d, idx) => ({
    [ID]: canUseIdentifier
      ? d[rowIdentifier]
      : TABLE_ROW_ID_DEFAULT(tableId, idx),
    [FORM_EXPANDED]: formExpanded(expandedForms, d, dataItems, rowIdentifier),
    [VISIBLE]: true,
    [SELECTED]: selectableDataPath
      ? convertNonBooleanIndicatorToBoolean(d[selectableDataPath])
      : false,
    ...d,
  }));
};

/**
 * Checks if all dataItems have a value at the specified rowIdentifier (if supplied)
 * @param dataItems
 * @param rowIdentifier
 */
export const canUseIdentifiers = <DataItem extends FluidTableRowData<DataItem>>(
  dataItems: DataItem[],
  rowIdentifier: keyof DataItem
) =>
  !!rowIdentifier &&
  dataItems.every((d) => !isNullOrUndefined(d[rowIdentifier])) &&
  Utils.isUniqueArray(dataItems.map((d) => d[rowIdentifier]));

/**
 * Get nested data at path
 * @param dataItem
 * @param header
 * @param dataPath
 * @param explicitBoolean
 */
/* todo: due to the recursive nature of this function, DataItem could be an
    object of nested objects which makes it impossible to infer a return type
 */
export const getDataAtPath = <DataItem extends Record<string, unknown>>(
  dataItem: DataItem,
  header: FluidTableHeader,
  dataPath?: string,
  explicitBoolean = false
) => {
  const dataPaths = dataPath
    ? dataPath.split(PATH_DELIM)
    : header?.dataPath
    ? header.dataPath.split(PATH_DELIM)
    : [''];

  return dataPaths.reduce((finalValue: unknown, currentPath) => {
    const hasValue = explicitBoolean
      ? isBooleanValue(finalValue[currentPath]) ||
        !isNullOrUndefined(finalValue[currentPath])
      : !isNullOrUndefined(finalValue[currentPath]);
    return hasValue ? finalValue[currentPath] : '';
  }, dataItem);
};

/**
 * Converts a data item (table row) to a form state to allow custom validation on editable cells to use table
 * data in its dependent field.
 * @param dataItem
 */
export const rowDataToFormState = (dataItem) => {
  const value = Object.keys(dataItem).reduce((val, key) => {
    val[key] = {
      dataPath: key,
      value: dataItem[key],
    } as FluidFormElementState;
    return val;
  }, {});
  return {
    value,
  } as FluidFormState;
};

/**
 * Checks if the given condition is true, and if so, returns the live version of the dataItems
 * @param host
 * @param dataItems
 * @param condition
 */
export function getComputedDataItems(
  host: Partial<FluidTable>,
  dataItems: FluidRowData[],
  condition: boolean
) {
  return condition
    ? dataItems.map((dataItem) =>
        getMutatedRowData(host._updatedRows, dataItem)
      )
    : dataItems;
}

/**
 * Get the mutated data for a given dataItem based off the original data, along with
 * any updated row data.
 * @param updatedRows
 * @param dataItem
 */
export const getMutatedRowData = (
  updatedRows: FluidKeyedObject<any>,
  dataItem: FluidRowData
) => {
  if (updatedRows[dataItem[ID]]) {
    const newData = Object.keys(updatedRows[dataItem[ID]]).reduce(
      (newData: {}, key: string) => {
        newData[key] = updatedRows[dataItem[ID]][key].newValue;
        return newData;
      },
      {}
    );
    return {
      ...dataItem,
      ...newData,
    };
  }
  return dataItem;
};

/**
 * Checks if the given 'fluidDataKey' is true on the given dataItem
 * @param dataItem - The dataItem to find and check against in the current _dataSource
 * @param dataSource - The current _dataSource to find dataItem in.
 * @param fluidDataKey - The FLUID added data key to check the value of.
 */
export const is = (
  dataItem: FluidRowData,
  dataSource: FluidRowData[],
  fluidDataKey: string
) => {
  return (dataSource.find((d) => d.rowId === dataItem.rowId) || {})[
    fluidDataKey
  ];
};

/**
 * Sets the value at a specific data path on a data item
 * @param dataItem
 * @param header
 * @param newValue
 * @param dataPath
 */
export const setDataAtPath = (
  dataItem: object,
  header: FluidTableHeader,
  newValue: any,
  dataPath?: string
): any => {
  const setNestedProp = (obj = {}, path: string[], value) => {
    if (path) {
      const [first, ...rest] = path;
      return {
        ...obj,
        [first]: rest.length ? setNestedProp(obj[first], rest, value) : value,
      };
    }
  };
  const dataPaths = dataPath
    ? dataPath.split(PATH_DELIM)
    : header?.dataPath?.split(PATH_DELIM);
  return setNestedProp(dataItem, dataPaths, newValue);
};

/**
 * Apply uniqueIds to each dataItem, so we can use them to monitor
 * expansion panel status (amongst other things)
 * @param host
 * @param dataItems
 */
export const prepareDataSource = <DataItem extends FluidTableRowData<DataItem>>(
  host: Partial<FluidTable>,
  dataItems: DataItem[]
) => {
  const start = performance.now();
  const preparedSource = applyTableReferences(
    dataItems,
    host.tableId,
    host.rowIdentifier as keyof DataItem,
    host._expandedForms,
    host.selectableDataPath
  );

  setGroupedDataSource(host, preparedSource);
  prepareExpandedForms(host, preparedSource, dataItems);
  Utils.logPerformance('prepareDataSource', start);
  return preparedSource;
};

const prepareExpandedForms = (host, preparedSource, dataItems) => {
  if (host.expandableForm) {
    host._expandedForms = (preparedSource || []).reduce(
      (expanded: { [key: string]: boolean }, current) => {
        expanded[current[ID]] = formExpanded(
          host._expandedForms,
          current,
          dataItems,
          host.rowIdentifier
        );
        return expanded;
      },
      {}
    );
  }
};
